import { createValuedColorsProvider, INumericValueColor, NumericRangeColorProvider, NumericValueColorProvider } from "@omnia/shared/color/provider"
import { IDatalayerMapInstance, IDatalayerViewerFactory, INumericValuesViewerOptions } from "@omnia/types";
import { LayerTypeId } from "../omnia";
import { BiomassUnit } from "@omnia/shared/generated/enums";
import { NumericValuesViewer } from "@omnia/shared/layers/viewers/numeric";

export class BiomassLayerViewerFactory {

    static create(): IDatalayerViewerFactory {

        var factory: IDatalayerViewerFactory = {
            typeId: LayerTypeId.Biomass,
            name: "Biomass",
            createViewer: layer => BiomassLayerViewerFactory.createViewer(layer)
        }

        return factory;
    }

    static createViewer(layer: IDatalayerMapInstance) {

        const createProvider = createValuedColorsProvider

        let colorProvider: NumericValueColorProvider
        let name: string = ""

        const unit = <BiomassUnit>layer.datalayer.Unit || 0

        switch (unit) {

            case BiomassUnit.NDVI:
                colorProvider = createProvider(ndviColors)
                name = "NDVI"
                break

            case BiomassUnit.YaraBiomassIndex:
                colorProvider = createProvider(yaraBiomassIndexColors)
                name = "Yara"
                break

            case BiomassUnit.CHI:
                colorProvider = createProvider(chiColors)
                name = "CHI"
                break

            case BiomassUnit.EVI:
                colorProvider = createProvider(eviColors)
                name = "EVI"
                break

            default:
                colorProvider = new NumericRangeColorProvider(ndviColors.map(i => i.Colour))
                break
        }


        let options = <INumericValuesViewerOptions>{
            colorProvider,
            units: null
        };

        return new BiomassLayerViewer(unit, name, layer, options)

    }

}

export class BiomassLayerViewer extends NumericValuesViewer {

    constructor(private unit: BiomassUnit | 0, private name: string, layer: IDatalayerMapInstance, options?: INumericValuesViewerOptions) {
        super(layer, options)
    }

    getKey() {

        const key = super.getKey()

        return {
            id: `biomass-${this.unit}`,
            name: this.name,
            ...key
        }
    }

}

export const ndviColors: INumericValueColor[] = [
    { Value: 0, Colour: [0x3f, 0x00, 0x00] },
    { Value: 0.05, Colour: [0x4f, 0x08, 0x00] },
    { Value: 0.1, Colour: [0x63, 0x14, 0x01] },
    { Value: 0.15, Colour: [0x76, 0x25, 0x02] },
    { Value: 0.2, Colour: [0x8a, 0x39, 0x03] },
    { Value: 0.25, Colour: [0x9d, 0x51, 0x05] },
    { Value: 0.3, Colour: [0xb1, 0x6d, 0x07] },
    { Value: 0.35, Colour: [0xc4, 0x8c, 0x09] },
    { Value: 0.4, Colour: [0xd8, 0xaf, 0x0b] },
    { Value: 0.45, Colour: [0xeb, 0xd5, 0x00] },
    { Value: 0.5, Colour: [0xff, 0xff, 0x10] },
    { Value: 0.55, Colour: [0xd3, 0xe9, 0x00] },
    { Value: 0.6, Colour: [0xab, 0xd4, 0x0b] },
    { Value: 0.65, Colour: [0x88, 0xbe, 0x08] },
    { Value: 0.7, Colour: [0x68, 0xa9, 0x06] },
    { Value: 0.75, Colour: [0x4c, 0x93, 0x04] },
    { Value: 0.8, Colour: [0x34, 0x7d, 0x03] },
    { Value: 0.85, Colour: [0x20, 0x68, 0x02] },
    { Value: 0.9, Colour: [0x11, 0x52, 0x01] },
    { Value: 0.95, Colour: [0x06, 0x3d, 0x00] },
    { Value: 1, Colour: [0x00, 0x28, 0x00] },
]

const yaraBiomassIndexColors: INumericValueColor[] = [
    { Value: 0, Colour: [0x3f, 0x00, 0x00] },
    { Value: 1, Colour: [0x4f, 0x08, 0x00] },
    { Value: 2, Colour: [0x63, 0x14, 0x01] },
    { Value: 3, Colour: [0x76, 0x25, 0x02] },
    { Value: 4, Colour: [0x8a, 0x39, 0x03] },
    { Value: 5, Colour: [0x9d, 0x51, 0x05] },
    { Value: 6, Colour: [0xb1, 0x6d, 0x07] },
    { Value: 7, Colour: [0xc4, 0x8c, 0x09] },
    { Value: 8, Colour: [0xd8, 0xaf, 0x0b] },
    { Value: 9, Colour: [0xeb, 0xd5, 0x00] },
    { Value: 10, Colour: [0xff, 0xff, 0x10] },
    { Value: 11, Colour: [0xd3, 0xe9, 0x00] },
    { Value: 12, Colour: [0xab, 0xd4, 0x0b] },
    { Value: 13, Colour: [0x88, 0xbe, 0x08] },
    { Value: 14, Colour: [0x68, 0xa9, 0x06] },
    { Value: 15, Colour: [0x4c, 0x93, 0x04] },
    { Value: 16, Colour: [0x34, 0x7d, 0x03] },
    { Value: 17, Colour: [0x20, 0x68, 0x02] },
    { Value: 18, Colour: [0x11, 0x52, 0x01] },
    { Value: 19, Colour: [0x06, 0x3d, 0x00] },
    { Value: 20, Colour: [0x00, 0x28, 0x00] },
]

// From:
// https://rampgenerator.com/?unique_colors=4&steps=31&step_color%5B1%5D=%23820202&at_step%5B1%5D=1&step_color%5B2%5D=%23F79900&at_step%5B2%5D=11&step_color%5B3%5D=%23E4FB08&at_step%5B3%5D=22&step_color%5B4%5D=%23198400&at_step%5B4%5D=35&min_value=0&max_value=12&decimals=1&opacity=1&col=COLUMN_NAME&units=&default_tab=TABLE&updated=1
/*
    with s as (
    select
    value Value,
    '[0x' + SUBSTRING(Color, 2, 2) + ', 0x' + SUBSTRING(Color, 4, 2) + ', 0x' + SUBSTRING(Color, 6, 2) + ']' Colour
    from OPENJSON(JSON_QUERY(@j, '$.ramp')) with (value numeric(5,1), color varchar(10))
)
select * from s
for json auto
*/
const chiColors: INumericValueColor[] = [
    {
        "Value": 0.0,
        "Colour": [0x82, 0x02, 0x02]
    },
    {
        "Value": 0.4,
        "Colour": [0x8D, 0x11, 0x01]
    },
    {
        "Value": 0.8,
        "Colour": [0x99, 0x20, 0x01]
    },
    {
        "Value": 1.2,
        "Colour": [0xA5, 0x2F, 0x01]
    },
    {
        "Value": 1.6,
        "Colour": [0xB0, 0x3E, 0x01]
    },
    {
        "Value": 2.0,
        "Colour": [0xBC, 0x4D, 0x01]
    },
    {
        "Value": 2.4,
        "Colour": [0xC8, 0x5C, 0x00]
    },
    {
        "Value": 2.8,
        "Colour": [0xD3, 0x6B, 0x00]
    },
    {
        "Value": 3.2,
        "Colour": [0xDF, 0x7A, 0x00]
    },
    {
        "Value": 3.6,
        "Colour": [0xEB, 0x89, 0x00]
    },
    {
        "Value": 4.0,
        "Colour": [0xF7, 0x99, 0x00]
    },
    {
        "Value": 4.4,
        "Colour": [0xF5, 0xA1, 0x00]
    },
    {
        "Value": 4.8,
        "Colour": [0xF3, 0xAA, 0x01]
    },
    {
        "Value": 5.2,
        "Colour": [0xF1, 0xB3, 0x02]
    },
    {
        "Value": 5.6,
        "Colour": [0xF0, 0xBC, 0x02]
    },
    {
        "Value": 6.0,
        "Colour": [0xEE, 0xC5, 0x03]
    },
    {
        "Value": 6.4,
        "Colour": [0xEC, 0xCE, 0x04]
    },
    {
        "Value": 6.8,
        "Colour": [0xEA, 0xD7, 0x05]
    },
    {
        "Value": 7.2,
        "Colour": [0xE9, 0xE0, 0x05]
    },
    {
        "Value": 7.6,
        "Colour": [0xE7, 0xE9, 0x06]
    },
    {
        "Value": 8.0,
        "Colour": [0xE5, 0xF2, 0x07]
    },
    {
        "Value": 8.4,
        "Colour": [0xE4, 0xFB, 0x08]
    },
    {
        "Value": 8.8,
        "Colour": [0xCD, 0xED, 0x07]
    },
    {
        "Value": 9.2,
        "Colour": [0xB6, 0xE0, 0x06]
    },
    {
        "Value": 9.6,
        "Colour": [0xA0, 0xD3, 0x05]
    },
    {
        "Value": 10.0,
        "Colour": [0x89, 0xC6, 0x04]
    },
    {
        "Value": 10.4,
        "Colour": [0x73, 0xB8, 0x03]
    },
    {
        "Value": 10.8,
        "Colour": [0x5C, 0xAB, 0x02]
    },
    {
        "Value": 11.2,
        "Colour": [0x46, 0x9E, 0x01]
    },
    {
        "Value": 11.6,
        "Colour": [0x2F, 0x91, 0x00]
    },
    {
        "Value": 12.0,
        "Colour": [0x19, 0x84, 0x00]
    }
]


const eviColors: INumericValueColor[] = [
    { Value: -1, Colour: [0x3f, 0x00, 0x00] },
    { Value: -0.5, Colour: [0x4f, 0x08, 0x00] },
    { Value: -0.2, Colour: [0x63, 0x14, 0x01] },
    { Value: -0.1, Colour: [0x76, 0x25, 0x02] },
    { Value: 0, Colour: [0x8a, 0x39, 0x03] },
    { Value: 0.05, Colour: [0x9d, 0x51, 0x05] },
    { Value: 0.075, Colour: [0xb1, 0x6d, 0x07] },
    { Value: 0.1, Colour: [0xc4, 0x8c, 0x09] },
    { Value: 0.125, Colour: [0xd8, 0xaf, 0x0b] },
    { Value: 0.15, Colour: [0xeb, 0xd5, 0x00] },
    { Value: 0.175, Colour: [0xff, 0xff, 0x10] },
    { Value: 0.2, Colour: [0xd3, 0xe9, 0x00] },
    { Value: 0.25, Colour: [0xab, 0xd4, 0x0b] },
    { Value: 0.3, Colour: [0x88, 0xbe, 0x08] },
    { Value: 0.35, Colour: [0x68, 0xa9, 0x06] },
    { Value: 0.4, Colour: [0x4c, 0x93, 0x04] },
    { Value: 0.45, Colour: [0x34, 0x7d, 0x03] },
    { Value: 0.5, Colour: [0x20, 0x68, 0x02] },
    { Value: 0.55, Colour: [0x11, 0x52, 0x01] },
    { Value: 0.6, Colour: [0x06, 0x3d, 0x00] },
    { Value: 0.8, Colour: [0x00, 0x28, 0x00] },
]
